/**
 * Contract Intelligence's home: what needs attention across the
 * organization's reviews — open gaps by severity, runs in progress, the
 * latest reviews and the counterparties with the most open gaps — or, before
 * the first review, what the app does and how to start.
 *
 * @module components/apps/contractIntelligence/CiOverviewPage
 */
import { Link } from "@tanstack/react-router";
import { ArrowRightIcon, Building2Icon, ClipboardListIcon, FileCheckIcon, PlusIcon, SendIcon } from "lucide-react";
import { useMemo, type ReactNode } from "react";

import { cn } from "../../../lib/utils";
import { useCiDocuments, useCiReviews, type CiReviewSummary } from "../../../state/contractIntelligence";
import { Button } from "../../ui/button";
import { groupByCounterparty, isActive } from "./ciModel";
import { CiListSkeleton, CiPage, CiSection, CiUnavailable, SeverityCounts } from "./ciParts";
import { CiReviewRow } from "./CiReviewRow";
import { useCiOrg } from "./useCiOrg";

const STEPS: ReadonlyArray<readonly [ReactNode, string, string]> = [
  [<FileCheckIcon key="a" aria-hidden className="size-4" />, "Add documents", "Side letters, administration agreements, onboarding packs — PDF, Word or text."],
  [<ClipboardListIcon key="b" aria-hidden className="size-4" />, "Choose what to check against", "Your policies, a regime you follow, or one of your templates."],
  [<SendIcon key="c" aria-hidden className="size-4" />, "Decide each gap", "Every gap shows the flagged passage and the rule it misses. Accept it, or raise it as a finding in Governance."],
];

function StatTile({ label, value, detail, className }: { readonly label: string; readonly value: ReactNode; readonly detail?: ReactNode; readonly className?: string }) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1 rounded-xl border border-border/70 bg-card px-4 py-3", className)}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-2xl font-semibold tabular-nums tracking-[-0.02em]">{value}</span>
      <span className="min-h-4 text-xs text-muted-foreground">{detail}</span>
    </div>
  );
}

function totals(reviews: ReadonlyArray<CiReviewSummary>) {
  const open = { critical: 0, high: 0, medium: 0, low: 0 };
  let raised = 0;
  let accepted = 0;
  for (const review of reviews) {
    for (const key of Object.keys(open) as Array<keyof typeof open>) open[key] += review.gaps.open[key];
    raised += review.gaps.raised;
    accepted += review.gaps.accepted;
  }
  const monthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  return {
    open,
    openTotal: open.critical + open.high + open.medium + open.low,
    raised,
    accepted,
    inProgress: reviews.filter((review) => isActive(review.status)).length,
    lastMonth: reviews.filter((review) => Date.parse(review.createdAt) >= monthAgo).length,
  };
}

export function CiOverviewPage() {
  const { organizationId, orgSegment, orgName, canManage } = useCiOrg();
  const reviews = useCiReviews(organizationId);
  const documents = useCiDocuments(organizationId);
  const list = reviews.read?.kind === "ready" ? reviews.read.value : null;
  const summary = useMemo(() => (list ? totals(list) : null), [list]);
  const counterparties = useMemo(
    () =>
      documents.read?.kind === "ready"
        ? groupByCounterparty(documents.read.value)
            .filter((group) => group.counterparty !== null)
            .sort((a, b) => b.openGaps - a.openGaps || b.documents.length - a.documents.length)
            .slice(0, 6)
        : [],
    [documents.read],
  );

  const newReview = orgSegment ? (
    <Button size="sm" className="gap-1.5" render={<Link to="/$org/apps/contract-intelligence/new" params={{ org: orgSegment }} />}>
      <PlusIcon aria-hidden className="size-3.5" />
      New review
    </Button>
  ) : null;

  return (
    <CiPage title="Contract Intelligence" actions={newReview} data-ci-page="overview">
      {reviews.read === null ? (
        <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="h-[88px] animate-pulse rounded-xl border border-border/60 bg-muted/30" />
            ))}
          </div>
          <CiListSkeleton rows={5} />
        </div>
      ) : reviews.read.kind !== "ready" ? (
        <CiUnavailable what="Reviews" onRetry={reviews.reload} />
      ) : list && list.length === 0 ? (
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 py-12 sm:px-6" data-ci-intro="">
          <div className="space-y-3">
            <span className="inline-flex size-10 items-center justify-center rounded-xl border border-border/70 bg-card text-muted-foreground">
              <FileCheckIcon aria-hidden className="size-5" />
            </span>
            <h2 className="text-2xl font-semibold tracking-[-0.02em]">Check contracts against what {orgName ?? "you"} follows</h2>
            <p className="max-w-xl text-sm text-muted-foreground">
              Amoreg reads each document clause by clause against your policies, a regime or a template, and shows every gap with
              the passage that causes it. Nothing reaches Governance until a person raises it.
            </p>
          </div>
          <ol className="grid gap-3 sm:grid-cols-3" aria-label="How it works">
            {STEPS.map(([icon, title, detail], index) => (
              <li key={title} className="flex flex-col gap-2 rounded-xl border border-border/70 bg-card p-4">
                <span className="flex items-center gap-2 text-muted-foreground">
                  {icon}
                  <span className="text-[11px] font-medium tabular-nums">Step {index + 1}</span>
                </span>
                <span className="text-[13px] font-medium">{title}</span>
                <span className="text-xs text-muted-foreground">{detail}</span>
              </li>
            ))}
          </ol>
          {orgSegment ? (
            <div className="flex flex-wrap gap-2">
              <Button className="gap-1.5" render={<Link to="/$org/apps/contract-intelligence/new" params={{ org: orgSegment }} />}>
                <PlusIcon aria-hidden className="size-4" />
                Start a review
              </Button>
              <Button variant="ghost" render={<Link to="/$org/apps/contract-intelligence/templates" params={{ org: orgSegment }} />}>
                {canManage ? "Set up a template" : "See templates"}
              </Button>
            </div>
          ) : null}
        </div>
      ) : list && summary && orgSegment ? (
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-6 sm:px-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-ci-stats="">
            <StatTile
              label="Open gaps"
              value={summary.openTotal}
              detail={summary.openTotal > 0 ? <SeverityCounts open={summary.open} /> : "Nothing waiting on a decision"}
            />
            <StatTile label="In progress" value={summary.inProgress} detail={summary.inProgress > 0 ? "Results arrive as they are found" : "No reviews running"} />
            <StatTile label="Reviews" value={list.length} detail={`${summary.lastMonth} in the last 30 days`} />
            <StatTile label="Raised to Governance" value={summary.raised} detail={`${summary.accepted} accepted`} />
          </div>

          {summary.inProgress > 0 ? (
            <CiSection title="In progress" meta={summary.inProgress}>
              <div className="overflow-hidden rounded-xl border border-border/70 bg-card">
                {list.filter((review) => isActive(review.status)).map((review) => (
                  <CiReviewRow key={review.id} review={review} orgSegment={orgSegment} />
                ))}
              </div>
            </CiSection>
          ) : null}

          <CiSection
            title="Recent reviews"
            action={
              <Link
                to="/$org/apps/contract-intelligence/reviews"
                params={{ org: orgSegment }}
                className="inline-flex items-center gap-1 rounded-md px-1 text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              >
                All reviews
                <ArrowRightIcon aria-hidden className="size-3" />
              </Link>
            }
          >
            <div className="divide-y divide-border/50 overflow-hidden rounded-xl border border-border/70 bg-card">
              {list
                .filter((review) => !isActive(review.status))
                .slice(0, 8)
                .map((review) => (
                  <CiReviewRow key={review.id} review={review} orgSegment={orgSegment} />
                ))}
            </div>
          </CiSection>

          {counterparties.length > 0 ? (
            <CiSection
              title="Counterparties"
              action={
                <Link
                  to="/$org/apps/contract-intelligence/documents"
                  params={{ org: orgSegment }}
                  className="inline-flex items-center gap-1 rounded-md px-1 text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                >
                  All documents
                  <ArrowRightIcon aria-hidden className="size-3" />
                </Link>
              }
            >
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {counterparties.map((group) => (
                  <li key={group.key}>
                    <Link
                      to="/$org/apps/contract-intelligence/documents"
                      params={{ org: orgSegment }}
                      className="flex items-center gap-3 rounded-xl border border-border/70 bg-card px-4 py-3 outline-none transition-colors hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                        <Building2Icon aria-hidden className="size-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium">{group.counterparty}</span>
                        <span className="block text-xs text-muted-foreground">
                          {group.documents.length} document{group.documents.length === 1 ? "" : "s"}
                          {group.openGaps > 0 ? ` · ${group.openGaps} open gap${group.openGaps === 1 ? "" : "s"}` : ""}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </CiSection>
          ) : null}
        </div>
      ) : null}
    </CiPage>
  );
}
